import {
  ensureBinarySafeGrpcHarRequest,
  hasHarDecodedBodyView,
  isDecodedGrpcJsonHarBody,
  parseBinaryBody,
  prepareGrpcHarRequestForPreview,
  prepareGrpcHarResponseForPreview,
  resolveGrpcHarPreviewResponse,
  resolveStoredHarResponseDisplayContent,
  type HarGrpcBodyView,
  type StoredHarDisplayEntry,
} from '@terra/shared-utils';
import { resolveHarEntryRequestDisplayContent } from '@terra/shared-utils';
import {
  isBinaryGrpcWebOrConnectContentType,
  isGrpcWebTextContentType,
  isTextMimeType,
} from '@terra/shared-asset-processing';
import type {
  HarEntryDecodeStatusType,
  JsonValueType,
  WebGrpcDecodedData,
} from '@terra/shared-schemas-tracker';
import { JsonValueSchema } from '@terra/shared-schemas-tracker';
import { portalDecodeGrpcHarApi } from '../clients/tester-client/portal/portal-decode-grpc-har-api';
import { parseRequestFromText } from './httpFormatter';
import {
  BINARY_BODY_PREFIX,
  encodeBinaryBody,
  getContentTypeHeader,
} from '../electron-shared/utils/binary-body';
import type { BodyEncoding } from '../electron-shared/utils/binary-body';

const PARTIAL_DECODE_PREFIX = '[partial decode — not all fields recovered]\n';

export function hasUsableResponseWebGrpcDecode(
  webGrpcDecodedData: WebGrpcDecodedData | null | undefined,
): boolean {
  if (!webGrpcDecodedData) {
    return false;
  }

  if (webGrpcDecodedData.decodedResponse != null) {
    return true;
  }

  return (
    webGrpcDecodedData.status === 'PARTIAL' && Boolean(webGrpcDecodedData.error)
  );
}

export function resolveEffectiveResponseWebGrpcDecodedData({
  replayDecode,
}: {
  replayDecode?: WebGrpcDecodedData | null;
  originalDecode?: WebGrpcDecodedData | null;
}): WebGrpcDecodedData | null {
  if (hasUsableResponseWebGrpcDecode(replayDecode)) {
    return replayDecode ?? null;
  }

  return null;
}

export function normalizeReplayResponseBodyForHar({
  body,
  headers,
}: {
  body: string;
  headers: Record<string, string | string[]>;
}): string {
  if (body.startsWith(BINARY_BODY_PREFIX)) {
    return body;
  }

  const { body: parsedBody, bodyEncoding } = parseBinaryBody(body);

  if (bodyEncoding === 'base64') {
    return body;
  }

  const contentType = getContentTypeHeader(headers);

  if (
    isBinaryGrpcWebOrConnectContentType(contentType) ||
    looksLikeGrpcWebBinaryFrame(parsedBody)
  ) {
    return encodeBinaryBody(Buffer.from(parsedBody, 'latin1'));
  }

  return body;
}

function looksLikeGrpcWebBinaryFrame(body: string): boolean {
  if (body.length < 5) {
    return false;
  }

  const buffer = Buffer.from(body, 'latin1');
  const flag = buffer[0];

  if (flag !== 0 && flag !== 1) {
    return false;
  }

  const messageLength = buffer.readUInt32BE(1);

  return messageLength > 0 && messageLength <= buffer.length - 5;
}

type ReplayResponseLike = {
  statusCode: number;
  statusMessage: string;
  headers: Record<string, string | string[]>;
  body: string;
  httpVersion?: string;
};

type ReplaySessionRequestLike = {
  method: string;
  url: string;
  headers: Record<string, string | string[]>;
  body?: string;
  bodyEncoding?: BodyEncoding;
};

function headersRecordToHarHeaders(
  headers: Record<string, string | string[]>,
): Array<{ name: string; value: string }> {
  return Object.entries(headers).flatMap(([name, value]) =>
    Array.isArray(value)
      ? value.map((entry) => ({ name, value: entry }))
      : [{ name, value }],
  );
}

function toHarRequestFromSession({
  grpcHarDisplay,
  sessionRequest,
}: {
  grpcHarDisplay: StoredHarDisplayEntry;
  sessionRequest: ReplaySessionRequestLike;
}): StoredHarDisplayEntry['request'] {
  const existingRequest =
    typeof grpcHarDisplay.request === 'object' &&
    grpcHarDisplay.request !== null
      ? (grpcHarDisplay.request as Record<string, unknown>)
      : {};
  const existingPostData =
    typeof existingRequest.postData === 'object' &&
    existingRequest.postData !== null
      ? (existingRequest.postData as Record<string, unknown>)
      : {};
  const contentType = getContentTypeHeader(sessionRequest.headers);

  return prepareGrpcHarRequestForPreview({
    ...existingRequest,
    method: sessionRequest.method,
    url: sessionRequest.url,
    headers: headersRecordToHarHeaders(sessionRequest.headers),
    postData: {
      ...existingPostData,
      mimeType:
        typeof existingPostData.mimeType === 'string'
          ? existingPostData.mimeType
          : contentType,
      text: sessionRequest.body ?? '',
      encoding: sessionRequest.bodyEncoding === 'base64' ? 'base64' : undefined,
    },
  });
}

export function buildReplayResponseHarDisplayEntry({
  grpcHarDisplay,
  response,
  webGrpcDecodedData,
}: {
  grpcHarDisplay: StoredHarDisplayEntry;
  response: ReplayResponseLike;
  webGrpcDecodedData?: WebGrpcDecodedData | null;
}): StoredHarDisplayEntry {
  const contentType = getContentTypeHeader(response.headers);
  const normalizedBody = normalizeReplayResponseBodyForHar({
    body: response.body,
    headers: response.headers,
  });

  return {
    request: grpcHarDisplay.request,
    response: prepareGrpcHarResponseForPreview({
      status: response.statusCode,
      statusText: response.statusMessage,
      httpVersion: response.httpVersion,
      headers: headersRecordToHarHeaders(response.headers),
      content: {
        mimeType: contentType,
        text: normalizedBody,
      },
    }),
    webGrpcDecodedData: resolveEffectiveResponseWebGrpcDecodedData({
      replayDecode: webGrpcDecodedData,
    }),
  };
}

export function buildGrpcHarPreviewPayload({
  applicationId,
  grpcHarDisplay,
  sessionRequest,
  requestData,
  response,
  sentRequestWire,
  sentRequestWireEncoding,
}: {
  applicationId: string;
  grpcHarDisplay: StoredHarDisplayEntry;
  sessionRequest: ReplaySessionRequestLike;
  requestData?: string;
  response: ReplayResponseLike;
  sentRequestWire?: string;
  sentRequestWireEncoding?: BodyEncoding;
}): {
  applicationId: string;
  request: StoredHarDisplayEntry['request'];
  response: StoredHarDisplayEntry['response'];
} {
  const responseHarDisplay = buildReplayResponseHarDisplayEntry({
    grpcHarDisplay,
    response,
  });
  const requestFromEditor =
    requestData &&
    buildHarRequestFromEditor({
      requestData,
      grpcHarDisplay,
      originalUrl: sessionRequest.url,
      wireBody: sentRequestWire,
      wireBodyEncoding: sentRequestWireEncoding,
    });

  return {
    applicationId,
    request: prepareGrpcHarPreviewPayload({
      request:
        requestFromEditor ??
        toHarRequestFromSession({
          grpcHarDisplay,
          sessionRequest: sentRequestWire
            ? {
                ...sessionRequest,
                body: sentRequestWire,
                bodyEncoding: sentRequestWireEncoding,
              }
            : sessionRequest,
        }),
    }),
    response: prepareStoredHarResponseForGrpcPreview(
      responseHarDisplay.response,
    ),
  };
}

function prepareGrpcHarPreviewPayload({
  request,
}: {
  request: StoredHarDisplayEntry['request'];
}): StoredHarDisplayEntry['request'] {
  if (
    typeof request !== 'object' ||
    request === null ||
    Array.isArray(request)
  ) {
    return request;
  }

  return prepareGrpcHarRequestForPreview(
    request as Parameters<typeof prepareGrpcHarRequestForPreview>[0],
  ) as StoredHarDisplayEntry['request'];
}

function prepareStoredHarResponseForGrpcPreview(
  response: StoredHarDisplayEntry['response'],
): StoredHarDisplayEntry['response'] {
  if (
    typeof response !== 'object' ||
    response === null ||
    Array.isArray(response)
  ) {
    return response;
  }

  return prepareGrpcHarResponseForPreview(
    response as Parameters<typeof prepareGrpcHarResponseForPreview>[0],
  ) as StoredHarDisplayEntry['response'];
}

export async function fetchReplayResponseWebGrpcDecode({
  applicationId,
  grpcHarDisplay,
  sessionRequest,
  requestData,
  sentRequestWire,
  sentRequestWireEncoding,
  response,
}: {
  applicationId: string;
  grpcHarDisplay: StoredHarDisplayEntry;
  sessionRequest: ReplaySessionRequestLike;
  requestData: string;
  sentRequestWire: string;
  sentRequestWireEncoding?: BodyEncoding;
  response: ReplayResponseLike;
}): Promise<WebGrpcDecodedData | null> {
  const normalizedResponse = {
    ...response,
    body: normalizeReplayResponseBodyForHar({
      body: response.body,
      headers: response.headers,
    }),
  };

  const decoded = await portalDecodeGrpcHarApi.preview(
    buildGrpcHarPreviewPayload({
      applicationId,
      grpcHarDisplay,
      sessionRequest,
      requestData,
      sentRequestWire,
      sentRequestWireEncoding,
      response: normalizedResponse,
    }),
  );

  if (!hasUsableResponseWebGrpcDecode(decoded.webGrpcDecodedData)) {
    return null;
  }

  return decoded.webGrpcDecodedData ?? null;
}

export function resolveReplayRequestDisplayContent({
  grpcHarDisplay,
  bodyView,
  fallbackRequestData,
}: {
  grpcHarDisplay?: StoredHarDisplayEntry;
  bodyView: HarGrpcBodyView;
  fallbackRequestData: string;
}): string {
  return resolveHarEntryRequestDisplayContent({
    harEntry: grpcHarDisplay,
    bodyView,
    fallbackContent: fallbackRequestData,
  });
}

export function resolveReplayResponseDisplayContent({
  grpcHarDisplay,
  response,
  bodyView,
  webGrpcDecodedData,
  fallbackFormattedResponse,
}: {
  grpcHarDisplay?: StoredHarDisplayEntry;
  response: ReplayResponseLike | null;
  bodyView: HarGrpcBodyView;
  webGrpcDecodedData?: WebGrpcDecodedData | null;
  fallbackFormattedResponse: string;
}): string {
  const effectiveWebGrpcDecodedData = hasUsableResponseWebGrpcDecode(
    webGrpcDecodedData,
  )
    ? webGrpcDecodedData
    : undefined;

  if (!grpcHarDisplay || !response) {
    return fallbackFormattedResponse;
  }

  const normalizedResponse = {
    ...response,
    body: normalizeReplayResponseBodyForHar({
      body: response.body,
      headers: response.headers,
    }),
  };

  const harEntry = buildReplayResponseHarDisplayEntry({
    grpcHarDisplay,
    response: normalizedResponse,
    webGrpcDecodedData: effectiveWebGrpcDecodedData,
  });

  const hasResponseDecode = hasUsableResponseWebGrpcDecode(
    harEntry.webGrpcDecodedData,
  );

  if (!hasHarDecodedBodyView({ harEntry })) {
    return fallbackFormattedResponse;
  }

  if (bodyView === 'decoded' && !hasResponseDecode) {
    return fallbackFormattedResponse;
  }

  return resolveStoredHarResponseDisplayContent({
    harEntry,
    bodyView,
  });
}

export function replaceHttpEditorBody({
  message,
  body,
}: {
  message: string;
  body: string;
}): string {
  const separatorIndex = message.indexOf('\n\n');
  const head =
    separatorIndex === -1 ? message : message.slice(0, separatorIndex);

  return body ? `${head}\n\n${body}` : head;
}

export function resolveEditorWireBody(
  body: string,
  options?: {
    contentType?: string;
  },
): { wireBody: string; wireBodyEncoding: BodyEncoding } | null {
  const trimmed = body.trim();

  if (!trimmed) {
    return null;
  }

  const { body: parsedBody, bodyEncoding } = parseBinaryBody(trimmed);

  if (bodyEncoding === 'base64' && parsedBody) {
    return { wireBody: parsedBody, wireBodyEncoding: 'base64' };
  }

  const declaredContentType = options?.contentType?.trim() ?? '';
  const allowRawBase64Heuristic =
    declaredContentType.length === 0 || !isTextMimeType(declaredContentType);

  if (!allowRawBase64Heuristic) {
    return null;
  }

  const normalized = trimmed.replace(/\s/g, '');

  if (normalized.length >= 8 && /^[A-Za-z0-9+/=]+$/.test(normalized)) {
    return { wireBody: normalized, wireBodyEncoding: 'base64' };
  }

  return null;
}

type DecodedGrpcJsonBody = Record<string, JsonValueType>;

export function parseEditedGrpcDecodedJsonBody(
  body: string,
): DecodedGrpcJsonBody | null {
  const trimmed = body.trim().replace(/^\uFEFF/, '');

  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith(PARTIAL_DECODE_PREFIX)) {
    return null;
  }

  const candidates = [trimmed, extractJsonObjectText(trimmed)].filter(
    (value): value is string => Boolean(value),
  );

  for (const jsonText of candidates) {
    try {
      const parsed: unknown = JSON.parse(jsonText);
      const validated = JsonValueSchema.safeParse(parsed);

      if (
        !validated.success ||
        typeof validated.data !== 'object' ||
        validated.data === null ||
        Array.isArray(validated.data)
      ) {
        continue;
      }

      return validated.data as DecodedGrpcJsonBody;
    } catch {
      continue;
    }
  }

  return null;
}

function extractJsonObjectText(body: string): string | null {
  const start = body.indexOf('{');
  const end = body.lastIndexOf('}');

  if (start === -1 || end === -1 || end <= start) {
    return null;
  }

  return body.slice(start, end + 1);
}

function formatGrpcDecodedBodyForEditor({
  decodedRequest,
  decodeStatus,
  decodeError,
}: {
  decodedRequest: unknown;
  decodeStatus?: HarEntryDecodeStatusType | null;
  decodeError?: string | null;
}): string {
  if (
    (decodedRequest === null || decodedRequest === undefined) &&
    decodeStatus === 'PARTIAL' &&
    decodeError
  ) {
    return `${PARTIAL_DECODE_PREFIX}${decodeError}`;
  }

  if (
    typeof decodedRequest !== 'object' ||
    decodedRequest === null ||
    Array.isArray(decodedRequest)
  ) {
    return decodeError ?? '';
  }

  const json = JSON.stringify(decodedRequest, null, 2);

  return decodeStatus === 'PARTIAL' ? `${PARTIAL_DECODE_PREFIX}${json}` : json;
}

function buildHarHttpRequestFromEditor({
  requestData,
  grpcHarDisplay,
  originalUrl,
}: {
  requestData: string;
  grpcHarDisplay: StoredHarDisplayEntry;
  originalUrl: string;
}): {
  method: string;
  url: string;
  httpVersion?: string;
  headers: Array<{ name: string; value: string }>;
  postData?: {
    mimeType?: string;
    text?: string;
    encoding?: string;
    comment?: string;
  };
} | null {
  const parsed = parseRequestFromText({ text: requestData, originalUrl });

  if (!parsed) {
    return null;
  }

  const existingRequest =
    typeof grpcHarDisplay.request === 'object' &&
    grpcHarDisplay.request !== null
      ? (grpcHarDisplay.request as Record<string, unknown>)
      : {};
  const existingPostData =
    typeof existingRequest.postData === 'object' &&
    existingRequest.postData !== null
      ? (existingRequest.postData as Record<string, unknown>)
      : {};
  const contentType =
    getContentTypeHeader(parsed.headers) ||
    (typeof existingPostData.mimeType === 'string'
      ? existingPostData.mimeType
      : 'application/grpc-web+proto');
  const { body: wireBody, bodyEncoding } = parseBinaryBody(parsed.body ?? '');

  return {
    method: parsed.method,
    url: parsed.url,
    httpVersion: parsed.httpVersion,
    headers: headersRecordToHarHeaders(parsed.headers),
    postData: {
      ...existingPostData,
      mimeType: contentType,
      text: wireBody,
      encoding: bodyEncoding === 'base64' ? 'base64' : undefined,
    },
  };
}

export function buildHarRequestFromEditor({
  requestData,
  grpcHarDisplay,
  originalUrl,
  wireBody,
  wireBodyEncoding,
}: {
  requestData: string;
  grpcHarDisplay: StoredHarDisplayEntry;
  originalUrl: string;
  wireBody?: string;
  wireBodyEncoding?: BodyEncoding;
}): StoredHarDisplayEntry['request'] | null {
  const request = buildHarHttpRequestFromEditor({
    requestData,
    grpcHarDisplay,
    originalUrl,
  });

  if (!request) {
    return null;
  }

  if (!wireBody) {
    if (isDecodedGrpcJsonHarBody(request.postData?.text)) {
      return request;
    }

    return prepareGrpcHarRequestForPreview(request);
  }

  return prepareGrpcHarRequestForPreview({
    ...request,
    postData: {
      ...request.postData,
      text: wireBody,
      encoding: wireBodyEncoding === 'base64' ? 'base64' : undefined,
    },
  });
}

function formatWireRequestEditorBody({
  wireBody,
}: {
  wireBody: string;
}): string {
  return wireBody;
}

function looksLikeGrpcDecodedJsonEditorBody(body: string): boolean {
  const trimmed = body.trim().replace(/^\uFEFF/, '');

  if (!trimmed) {
    return false;
  }

  if (trimmed.startsWith(PARTIAL_DECODE_PREFIX)) {
    return true;
  }

  return trimmed.startsWith('{') || trimmed.startsWith('[');
}

export function resolveReplayTransformWireBody({
  requestData,
  originalUrl,
  storedWireBody,
  storedWireBodyEncoding,
}: {
  requestData: string;
  originalUrl: string;
  storedWireBody?: string;
  storedWireBodyEncoding?: BodyEncoding;
}): { wireBody: string; wireBodyEncoding?: BodyEncoding } | null {
  const parsed = parseRequestFromText({ text: requestData, originalUrl });

  if (!parsed) {
    if (storedWireBodyEncoding === 'base64' && storedWireBody) {
      return {
        wireBody: storedWireBody,
        wireBodyEncoding: storedWireBodyEncoding,
      };
    }

    return null;
  }

  const contentType = getContentTypeHeader(parsed.headers);
  const editorBody = parsed.body ?? '';
  const editorWire = editorBody
    ? resolveEditorWireBody(editorBody, { contentType })
    : null;

  if (editorWire) {
    return editorWire;
  }

  if (
    editorBody &&
    isGrpcWebTextContentType(contentType) &&
    !looksLikeGrpcDecodedJsonEditorBody(editorBody)
  ) {
    return { wireBody: editorBody };
  }

  if (storedWireBodyEncoding === 'base64' && storedWireBody) {
    return {
      wireBody: storedWireBody,
      wireBodyEncoding: storedWireBodyEncoding,
    };
  }

  return null;
}

export async function transformReplayRequestBodyView({
  applicationId,
  grpcHarDisplay,
  requestData,
  originalUrl,
  fromView,
  toView,
  storedWireBody,
  storedWireBodyEncoding,
}: {
  applicationId: string;
  grpcHarDisplay: StoredHarDisplayEntry;
  requestData: string;
  originalUrl: string;
  fromView: HarGrpcBodyView;
  toView: HarGrpcBodyView;
  storedWireBody?: string;
  storedWireBodyEncoding?: BodyEncoding;
}): Promise<{
  requestData: string;
  wireBody: string;
  wireBodyEncoding?: BodyEncoding;
  grpcHarDisplay: StoredHarDisplayEntry;
}> {
  const resolvedWire = resolveReplayTransformWireBody({
    requestData,
    originalUrl,
    storedWireBody,
    storedWireBodyEncoding,
  });
  const harRequest = buildHarRequestFromEditor({
    requestData,
    grpcHarDisplay,
    originalUrl,
    wireBody: resolvedWire?.wireBody,
    wireBodyEncoding: resolvedWire?.wireBodyEncoding,
  });

  if (!harRequest) {
    throw new Error('Failed to parse replay request');
  }

  const harHttpRequest = buildHarHttpRequestFromEditor({
    requestData,
    grpcHarDisplay,
    originalUrl,
  });

  if (!harHttpRequest) {
    throw new Error('Failed to parse replay request');
  }

  if (toView === 'decoded') {
    const preview = await portalDecodeGrpcHarApi.preview({
      applicationId,
      request: harRequest,
      response: resolveGrpcHarPreviewResponse(grpcHarDisplay.response),
    });
    const webGrpcDecodedData = preview.webGrpcDecodedData;

    if (!webGrpcDecodedData?.decodedRequest) {
      throw new Error(
        webGrpcDecodedData?.error ?? 'Failed to decode gRPC request body',
      );
    }

    const decodedBody = formatGrpcDecodedBodyForEditor({
      decodedRequest: webGrpcDecodedData.decodedRequest,
      decodeStatus: webGrpcDecodedData.status,
      decodeError: webGrpcDecodedData.error,
    });
    const nextGrpcHarDisplay: StoredHarDisplayEntry = {
      request: harRequest,
      response: grpcHarDisplay.response,
      webGrpcDecodedData,
    };
    const wireBody =
      resolvedWire?.wireBody ?? harHttpRequest.postData?.text ?? '';
    const wireBodyEncoding =
      resolvedWire?.wireBodyEncoding ??
      (harHttpRequest.postData?.encoding === 'base64' ? 'base64' : undefined);

    return {
      requestData: replaceHttpEditorBody({
        message: requestData,
        body: decodedBody,
      }),
      wireBody,
      wireBodyEncoding,
      grpcHarDisplay: nextGrpcHarDisplay,
    };
  }

  const parsed = parseRequestFromText({ text: requestData, originalUrl });

  if (!parsed) {
    throw new Error('Failed to parse replay request');
  }

  const decodedRequest = parseEditedGrpcDecodedJsonBody(parsed.body ?? '');

  if (!decodedRequest) {
    const wireBody = resolvedWire?.wireBody ?? null;
    const wireBodyEncoding = resolvedWire?.wireBodyEncoding ?? undefined;

    if (wireBody && wireBodyEncoding === 'base64') {
      const nextHarHttpRequest = {
        ...harHttpRequest,
        postData: {
          ...harHttpRequest.postData,
          text: wireBody,
          encoding: 'base64' as const,
        },
      };
      const nextHarRequest = ensureBinarySafeGrpcHarRequest(nextHarHttpRequest);

      return {
        requestData: replaceHttpEditorBody({
          message: requestData,
          body: formatWireRequestEditorBody({ wireBody }),
        }),
        wireBody,
        wireBodyEncoding,
        grpcHarDisplay: {
          ...grpcHarDisplay,
          request: nextHarRequest,
        },
      };
    }

    throw new Error('Request body is not valid decoded gRPC JSON');
  }

  const encoded = await portalDecodeGrpcHarApi.encodePreview({
    applicationId,
    request: harRequest,
    response: resolveGrpcHarPreviewResponse(grpcHarDisplay.response),
    decodedRequest,
  });
  const nextHarHttpRequest = {
    ...harHttpRequest,
    postData: {
      ...harHttpRequest.postData,
      text: encoded.wireRequestText,
      encoding: encoded.wireRequestEncoding,
    },
  };
  const nextHarRequest = ensureBinarySafeGrpcHarRequest(nextHarHttpRequest);
  const nextGrpcHarDisplay: StoredHarDisplayEntry = {
    request: nextHarRequest,
    response: grpcHarDisplay.response,
    webGrpcDecodedData: grpcHarDisplay.webGrpcDecodedData
      ? {
          ...grpcHarDisplay.webGrpcDecodedData,
          decodedRequest,
          status: 'DECODED',
          error: null,
        }
      : null,
  };

  return {
    requestData: replaceHttpEditorBody({
      message: requestData,
      body: formatWireRequestEditorBody({ wireBody: encoded.wireRequestText }),
    }),
    wireBody: encoded.wireRequestText,
    wireBodyEncoding: encoded.wireRequestEncoding ?? 'base64',
    grpcHarDisplay: nextGrpcHarDisplay,
  };
}

export async function resolveGrpcReplaySendBody({
  applicationId,
  grpcHarDisplay,
  requestData,
  originalUrl,
  parsedBody,
  storedWireBody,
  storedWireBodyEncoding,
}: {
  applicationId: string;
  grpcHarDisplay: StoredHarDisplayEntry;
  requestData: string;
  originalUrl: string;
  parsedBody: string;
  storedWireBody?: string;
  storedWireBodyEncoding?: BodyEncoding;
}): Promise<{ body: string; bodyEncoding: BodyEncoding | undefined }> {
  const decodedRequest = parseEditedGrpcDecodedJsonBody(parsedBody);

  if (decodedRequest) {
    const harRequest = buildHarRequestFromEditor({
      requestData,
      grpcHarDisplay,
      originalUrl,
    });

    if (!harRequest) {
      throw new Error('Failed to parse replay request');
    }

    const encoded = await portalDecodeGrpcHarApi.encodePreview({
      applicationId,
      request: harRequest,
      response: resolveGrpcHarPreviewResponse(grpcHarDisplay.response),
      decodedRequest,
    });

    return {
      body: encoded.wireRequestText,
      bodyEncoding: encoded.wireRequestEncoding ?? 'base64',
    };
  }

  const parsedRequest = parseRequestFromText({
    text: requestData,
    originalUrl,
  });
  const contentType = parsedRequest
    ? getContentTypeHeader(parsedRequest.headers)
    : '';

  const editorWire = resolveEditorWireBody(parsedBody, { contentType });

  if (editorWire) {
    return {
      body: editorWire.wireBody,
      bodyEncoding: editorWire.wireBodyEncoding,
    };
  }

  if (looksLikeGrpcDecodedJsonEditorBody(parsedBody)) {
    throw new Error('Request body is not valid decoded gRPC JSON');
  }

  if (
    !isTextMimeType(contentType) &&
    storedWireBodyEncoding === 'base64' &&
    storedWireBody
  ) {
    return { body: storedWireBody, bodyEncoding: 'base64' };
  }

  return { body: parsedBody, bodyEncoding: undefined };
}
